// ============================================================================
// SCHOOL CHROME — the pieces all three School screens share.
//
// The header band, the Canvas sync chip, the search field, the breadcrumb, the pipeline chips,
// and the one component that decides what a screen says when its endpoint does not exist yet.
// ============================================================================

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { Lecture, SyncStatusRow } from '@app/shared'

import { useSyncConnector } from '../../lib/data'
import { sourcePing } from '../../lib/motion'
import { ValueSwap } from '../motion/ValueSwap'
import { HeaderBand } from '../HeaderBand'
import { Search } from '../icons'
import { ChevronLeft } from './icons'
import { agoPhrase, LECTURE_STATUS_LABEL } from './derive'

/**
 * A screen whose data never arrived must SAY which version brings it. An empty course grid is a
 * claim — "you are not enrolled in anything" — and remote.ts throws a named error carrying the
 * sentence instead, so this prints it.
 */
export function Unavailable({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
      <p className="max-w-[620px] text-body text-ink-3 text-pretty">
        {error instanceof Error ? error.message : 'This could not be loaded.'}
      </p>
    </div>
  )
}

/**
 * THE CANVAS SYNC CHIP, permanently in the header band on all three screens (6a rationale: "sync
 * time sits in the header permanently").
 *
 * It reports the connector's real state, and the three states read differently on purpose:
 * synced says when, error says what is stale, never says it has never run. A chip that is always
 * green is a chip nobody reads on the day it matters.
 *
 * 13c · SYNC, WITHOUT A SPINNER. When a sync actually lands, the dot pings once (one expanding
 * ring, fading out — never a loop, because a loop is a spinner in a different shape) and the
 * sentence swaps: the stale phrase lifts out, the fresh one rises in.
 *
 * WHAT DECIDES THAT SOMETHING LANDED IS THE DATA, NOT THE BUTTON. Both are keyed off what the
 * connector came back with — status, timestamp, error — and never off the mutation settling: a
 * sync that ran and changed nothing must leave this chip visibly untouched, because "only values
 * that changed animate" is 13c's actual rule and that stillness is the information. It is also
 * why the swap is keyed off the TIMESTAMP rather than off the rendered phrase: "synced 4 min ago"
 * becoming "synced 5 min ago" is a clock tick, not an arrival, and animating it would claim
 * something happened once a minute forever.
 *
 * Nothing pings on first paint. A value that is there because the screen just mounted is not news.
 */
export function CanvasSyncChip({
  rows,
  error,
  today,
  nowMinutes,
}: {
  rows: SyncStatusRow[] | undefined
  error: unknown
  today: string
  nowMinutes: number
}) {
  // Called unconditionally, ahead of the early returns below, so this component never calls a
  // hook conditionally — the same rule ConnectorCard's `sync` follows.
  const sync = useSyncConnector()

  const canvas = rows?.find((r) => r.source === 'canvas')
  // What the connector last said about itself. Everything 13c animates is keyed off THIS, so a
  // sync that changed nothing moves nothing.
  const arrival =
    canvas && !error
      ? `${canvas.status}:${canvas.lastSyncAt ?? ''}:${canvas.lastError ?? ''}`
      : null

  const dotNode = useRef<HTMLSpanElement>(null)
  /** `undefined` until the first answer arrives — the first answer is not an arrival. */
  const seen = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (arrival === null) return
    if (seen.current === arrival) return
    const first = seen.current === undefined
    seen.current = arrival
    if (!first) sourcePing(dotNode.current)
  }, [arrival])

  // In remote mode the sync-status read itself throws until V2. A chip that cannot know is not
  // rendered at all — inventing "synced" there would be the exact lie this screen is against.
  if (error || !rows) return null
  if (!canvas) return null

  const dot =
    canvas.status === 'ok'
      ? 'bg-live-dot'
      : canvas.status === 'error'
        ? 'bg-danger-dot'
        : 'bg-warning-dot'

  const text =
    canvas.status === 'ok' && canvas.lastSyncAt
      ? `Canvas synced ${agoPhrase(canvas.lastSyncAt, today, nowMinutes)}`
      : canvas.status === 'never'
        ? 'Canvas has never synced'
        : (canvas.lastError ?? 'Canvas stopped syncing')

  return (
    <div className="flex flex-none flex-col gap-1">
      <div className="flex flex-none items-center gap-2 whitespace-nowrap rounded-pill border border-line bg-surface px-[11px] py-[6px]">
        <span
          ref={dotNode}
          className={['size-[6px] flex-none rounded-full', dot].join(' ')}
          aria-hidden
        />
        <ValueSwap value={arrival ?? text} className="text-[11px] font-medium leading-none text-ink-2">
          {text}
        </ValueSwap>
        <span className="h-[11px] w-px bg-line-3" aria-hidden />
        {/* LIVE: POST /api/connections/canvas/sync — the same adapter the six-hourly cron and the
            Connections screen's own "Sync now" (ConnectorCard.tsx) run. A label swap while it is
            in flight, per the motion brief ("loading is a label swap, not a spinner on a
            button"); demo mode refuses in words (no cloud behind the fixture), and that refusal
            is the sentence below rather than a disabled button. */}
        <button
          type="button"
          disabled={sync.isPending}
          onClick={() => sync.mutate('canvas')}
          className="text-[11px] font-medium leading-none text-success-dot transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] disabled:cursor-wait disabled:opacity-70"
        >
          {sync.isPending ? 'Syncing…' : 'Sync now'}
        </button>
      </div>
      {sync.error && (
        <p className="max-w-[320px] text-[10.5px] leading-[1.4] text-danger text-pretty">
          {sync.error instanceof Error
            ? sync.error.message
            : 'That sync could not be started, and the reason did not come back with a sentence attached.'}
        </p>
      )}
    </div>
  )
}

/**
 * The header band's search field. Search is not a rail item — it rides this field (the
 * orchestrator decision in docs/design/FIDELITY.md) — and submitting hands the query to
 * `/search`, which is honest about landing in V4 rather than pretending to answer.
 */
export function SchoolSearchField({
  placeholder,
  scope,
  maxWidth = 320,
}: {
  placeholder: string
  /** Pre-seeded into the query so the scope the user was in is not silently dropped. */
  scope?: string
  maxWidth?: number
}) {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()

  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault()
        const q = [scope, query.trim()].filter(Boolean).join(' ')
        navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search')
      }}
      style={{ maxWidth, '--mo-rest-shadow': 'var(--shadow-field)' } as CSSProperties}
      className="mo-field flex min-w-0 flex-1 items-center gap-[10px] rounded-tile border border-line-2 bg-surface/[0.86] px-[14px] py-[10px] shadow-field"
    >
      <Search size={15} strokeWidth={1.8} className="flex-none text-microcopy" />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] leading-none text-ink outline-none placeholder:text-muted"
      />
    </form>
  )
}

/** '‹ School | COGSCI 175' — the detail screens' breadcrumb (README §Interactions). */
export function Breadcrumb({ to, parent, title }: { to: string; parent: string; title: string }) {
  return (
    <div className="flex flex-none items-center gap-[18px] whitespace-nowrap">
      <Link
        to={to}
        className="flex items-center gap-2 text-[12px] font-medium leading-none text-muted transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-ink"
      >
        <ChevronLeft size={15} />
        {parent}
      </Link>
      <span className="h-[14px] w-px bg-dash" aria-hidden />
      <span className="text-screen font-semibold">{title}</span>
    </div>
  )
}

/** The band, wearing School's own gradient. */
export function SchoolBand({ children }: { children: ReactNode }) {
  return <HeaderBand band="school">{children}</HeaderBand>
}

// ── Pipeline chips ──────────────────────────────────────────────────────────

/** The 9px ring the mockup draws for anything the desktop worker is mid-way through. */
export function Spinner() {
  return (
    <span
      className="size-[9px] flex-none rounded-full border-[1.5px] border-line-2 border-t-microcopy"
      aria-hidden
    />
  )
}

const STATUS_CHIP: Record<Lecture['status'], string> = {
  transcribed: 'bg-success-fill text-success font-semibold',
  processing: 'bg-surface-4 text-muted font-semibold',
  audio_uploaded: 'bg-info-fill text-info font-semibold',
  not_recorded: 'bg-surface-4 text-microcopy font-medium',
  failed: 'bg-danger-fill text-danger font-semibold',
}

/** The lecture row's status pill. Four states, four different sentences — never a blank. */
export function LectureStatusChip({ status }: { status: Lecture['status'] }) {
  return (
    <span
      className={[
        'flex flex-none items-center gap-[6px] whitespace-nowrap rounded-pill px-[9px] py-1 text-[10.5px] leading-none',
        STATUS_CHIP[status],
      ].join(' ')}
    >
      {status === 'transcribed' && (
        <span className="size-[5px] flex-none rounded-full bg-live-dot" aria-hidden />
      )}
      {status === 'processing' && <Spinner />}
      {LECTURE_STATUS_LABEL[status]}
    </span>
  )
}
